import React, { useContext, useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import * as customerAPI from '../api/customer';
import * as itrAPI from '../api/itr';
import * as superadminAPI from '../api/superadmin';
import * as subadminAPI from '../api/subadmin';
import API from '../api/axios';
import { IoMdClose, IoIosArrowBack } from "react-icons/io";

const GlobalSearchOverlay = () => {
    const { user, globalSearchQuery, setGlobalSearchQuery, showGlobalSearch, setShowGlobalSearch, isCollapsed } = useContext(AuthContext);
    const navigate = useNavigate();
    const [customers, setCustomers] = useState([]);
    const [itrs, setItrs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedCards, setExpandedCards] = useState(new Set());
    const [viewingDetail, setViewingDetail] = useState(null);
    const [detailType, setDetailType] = useState(null);
    const [searchView, setSearchView] = useState('customer'); // 'customer' or 'itr'

    const handleViewDetails = (type, data) => {
        setViewingDetail(data);
        setDetailType(type);
    };

    const closeDetailModal = () => {
        setViewingDetail(null);
        setDetailType(null);
    };

    const toggleExpand = (id) => {
        setExpandedCards(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    useEffect(() => {
        if (!showGlobalSearch) return;

        // For Superadmin, use debounced backend search
        if (user?.issuperadmin) {
            if (globalSearchQuery.trim().length < 2) {
                setCustomers([]);
                setItrs([]);
                return;
            }

            const debounceTimer = setTimeout(async () => {
                setLoading(true);
                try {
                    const data = await superadminAPI.getGlobalSearch(globalSearchQuery);
                    setCustomers(data.customers || []);
                    setItrs(data.itrs || []);
                } catch (error) {
                    console.error("Search error:", error);
                } finally {
                    setLoading(false);
                }
            }, 500);

            return () => clearTimeout(debounceTimer);
        } else {
            // For other roles, keep initial fetch (or we could optimize them too later)
            if (customers.length === 0 && itrs.length === 0) {
                fetchData();
            }
        }
    }, [globalSearchQuery, showGlobalSearch, user?.issuperadmin]);

    const fetchData = async () => {
        setLoading(true);
        try {
            let custData = [];
            let itrData = [];

            if (user?.issuperadmin) {
                // This branch is now handled by the debounced search above for new queries,
                // but we keep the structure for compatibility or initial state if needed.
                return;
            } else if (user?.issubadmin) {
                const response = await subadminAPI.getCustomersITR();
                const rawItrs = response.data || [];
                const customerMap = new Map();
                itrData = rawItrs.map(item => {
                    const custId = item.customer_id;
                    const pan = item.pan_number || item.customer_pan_number || item.Pan_Number;
                    const name = item.name || item.customer_name || item.Customer_Name;

                    const groupKey = custId ? `cust_${custId}` : (pan ? `pan_${pan}` : `itr_${item.itr_id || item.id}`);

                    if (!customerMap.has(groupKey)) {
                        customerMap.set(groupKey, {
                            id: custId || groupKey,
                            name: name,
                            pan_number: pan,
                            mobile_no: item.mobile_no || item.customer_mobile || item.Mobile_No,
                            mail_id: item.mail_id || item.customer_email || item.Email,
                            father_name: item.father_name || item.customer_father_name || item.Father_Name,
                            dob: item.dob || item.customer_dob || item.Date_Of_Birth,
                            adhar_number: item.adhar_number || item.customer_adhar_number || item.Adhar_Number,
                            address: item.address || item.customer_address || item.Address,
                            category: item.category || item.customer_category || item.Category,
                            account_number: item.account_number || item.customer_account_number || item.Account_Number,
                            ifsc_code: item.ifsc_code || item.customer_ifsc_code || item.Ifsc_Code
                        });
                    }
                    return {
                        ...item,
                        id: item.itr_id || item.id,
                        customer_id: custId || groupKey,
                        asst_year: item.asst_year || item.itr_asst_year,
                        status: item.status || item.itr_status,
                        pan_number: pan,
                        created_at: item.created_at || item.Assign_Date || item.assign_date || item.Register_Date || item.Date
                    };
                });
                custData = Array.from(customerMap.values());
            } else if (user?.isca) {
                const caId = user?.id;
                const response = await API.get(`/ca/assigned-itrs/${caId}`);
                const rawItrs = response.data || [];
                const customerMap = new Map();
                itrData = rawItrs.map(item => {
                    const custId = item.customer_id;
                    const panActual = item.pan_number || item.customer_pan_number || item.Pan_Number;
                    const name = item.name || item.customer_name || item.Customer_Name;

                    const groupKey = custId ? `cust_${custId}` : (panActual ? `pan_${panActual}` : `itr_${item.itr_id || item.id}`);

                    if (!customerMap.has(groupKey)) {
                        customerMap.set(groupKey, {
                            id: custId || groupKey,
                            name: name,
                            pan_number: panActual,
                            mobile_no: item.mobile_no || item.customer_mobile || item.Mobile_No,
                            mail_id: item.mail_id || item.customer_email || item.Email,
                            father_name: item.father_name || item.customer_father_name || item.Father_Name,
                            dob: item.dob || item.customer_dob || item.Date_Of_Birth,
                            adhar_number: item.adhar_number || item.customer_adhar_number || item.Adhar_Number,
                            address: item.address || item.customer_address || item.Address,
                            category: item.category || item.customer_category || item.Category,
                            account_number: item.account_number || item.customer_account_number || item.Account_Number,
                            ifsc_code: item.ifsc_code || item.customer_ifsc_code || item.Ifsc_Code
                        });
                    }
                    return {
                        ...item,
                        id: item.itr_id || item.id,
                        customer_id: custId || groupKey,
                        asst_year: item.asst_year || item.itr_asst_year,
                        status: item.status || item.itr_status,
                        pan_number: panActual,
                        created_at: item.created_at || item.Assign_Date || item.assign_date || item.Register_Date || item.Date
                    };
                });
                custData = Array.from(customerMap.values());
            } else {
                [custData, itrData] = await Promise.all([
                    customerAPI.getCustomers(),
                    itrAPI.getITRs()
                ]);
            }

            setCustomers(Array.isArray(custData) ? custData : []);
            setItrs(Array.isArray(itrData) ? itrData : []);
        } catch (error) {
            console.error("Error fetching search data:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        setGlobalSearchQuery('');
        setShowGlobalSearch(false);
    };

    const filteredResults = useMemo(() => {
        if (!globalSearchQuery.trim()) return [];

        // For Superadmin, results are already filtered by the backend
        if (user?.issuperadmin) {
            return customers;
        }

        const query = globalSearchQuery.trim().toLowerCase();

        // For each customer, check if they or any of their ITRs match the query
        return customers.map(customer => {
            const name = (customer.name || '').toString().toLowerCase();
            const pan = (customer.pan_number || '').toString().toLowerCase();
            const mobile = (customer.mobile_no || '').toString().toLowerCase();

            const customerMatches = name.includes(query) || pan.includes(query) || mobile.includes(query);

            const matchingItrs = itrs.filter(itr => {
                if (String(itr.customer_id) !== String(customer.id)) return false;

                const asstYear = (itr.asst_year || '').toString().toLowerCase();
                const status = (itr.status || '').toString().toLowerCase();
                const itrPan = (itr.pan_number || '').toString().toLowerCase();
                const ack = (itr.ack_no || '').toString().toLowerCase();

                return asstYear.includes(query) ||
                    status.includes(query) ||
                    itrPan.includes(query) ||
                    ack.includes(query);
            });

            // If either the customer matches OR any of their ITRs match, include this result
            if (customerMatches || matchingItrs.length > 0) {
                return {
                    ...customer,
                    itrs: itrs.filter(itr => String(itr.customer_id) === String(customer.id))
                };
            }
            return null;
        }).filter(Boolean);
    }, [globalSearchQuery, customers, itrs, user?.issuperadmin]);

    const filteredItrs = useMemo(() => {
        if (!globalSearchQuery.trim()) return [];

        // For Superadmin, results are already filtered by the backend
        if (user?.issuperadmin) {
            return itrs.map(itr => ({
                ...itr,
                customer_details: {
                    name: itr.customer_name,
                    mobile_no: itr.customer_mobile,
                    pan_number: itr.pan_number
                }
            }));
        }

        const query = globalSearchQuery.trim().toLowerCase();

        return itrs.filter(itr => {
            const asstYear = (itr.asst_year || '').toString().toLowerCase();
            const status = (itr.status || '').toString().toLowerCase();
            const itrPan = (itr.pan_number || '').toString().toLowerCase();
            const ack = (itr.ack_no || '').toString().toLowerCase();

            const cust = customers.find(c => String(c.id) === String(itr.customer_id));
            const custName = cust ? (cust.name || '').toString().toLowerCase() : '';
            const custMobile = cust ? (cust.mobile_no || '').toString().toLowerCase() : '';

            return asstYear.includes(query) ||
                status.includes(query) ||
                itrPan.includes(query) ||
                ack.includes(query) ||
                custName.includes(query) ||
                custMobile.includes(query);
        }).map(itr => {
            const cust = customers.find(c => String(c.id) === String(itr.customer_id));
            return { ...itr, customer_details: cust };
        });
    }, [globalSearchQuery, itrs, customers, user?.issuperadmin]);

    if (!showGlobalSearch) return null;

    return (
        <div className={`fixed inset-0 pt-16 bg-purple-100 ${isCollapsed ? 'md:ml-20' : 'md:ml-64'} z-40 overflow-y-auto animate-in fade-in duration-300`}>
            <div className="max-w-6xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-8 border-b pb-4">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={handleBack}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors flex items-center gap-2 text-purple-700 font-bold"
                        >
                            <IoIosArrowBack size={24} />
                            <span>Back</span>
                        </button>
                        <h2 className="text-2xl font-black text-gray-800">Search Results</h2>
                    </div>
                    <div className="text-sm font-bold text-gray-400 uppercase tracking-widest">
                        Query: <span className="text-purple-600">"{globalSearchQuery}"</span>
                    </div>
                </div>

                {user?.issuperadmin && (
                    <div className="flex justify-center mb-8">
                        <div className="bg-white p-1.5 border border-purple-200 rounded-full flex gap-2 shadow-sm relative">
                            <button
                                onClick={() => setSearchView('customer')}
                                className={`px-8 py-2.5 rounded-full font-bold text-sm transition-all flex items-center gap-2 ${searchView === 'customer' ? 'bg-purple-600 text-white shadow-md' : 'bg-transparent text-gray-500 hover:text-gray-800 hover:bg-gray-50'}`}
                            >
                                <i className="fas fa-users"></i> Customer View
                            </button>
                            <button
                                onClick={() => setSearchView('itr')}
                                className={`px-8 py-2.5 rounded-full font-bold text-sm transition-all flex items-center gap-2 ${searchView === 'itr' ? 'bg-purple-600 text-white shadow-md' : 'bg-transparent text-gray-500 hover:text-gray-800 hover:bg-gray-50'}`}
                            >
                                <i className="fas fa-file-invoice"></i> ITR View
                            </button>
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="flex flex-col items-center justify-center py-20 gap-4">
                        <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin"></div>
                        <p className="text-gray-500 font-bold">Searching records...</p>
                    </div>
                ) : searchView === 'customer' ? (
                    filteredResults.length > 0 ? (
                        <div className="grid gap-6">
                            {filteredResults.map(customer => (
                                <div key={customer.id} className="bg-white border rounded-3xl shadow-sm hover:shadow-md transition-all overflow-hidden border-gray-100 mb-2">
                                    <div className="pt-6 px-6 flex flex-col md:flex-row justify-between gap-6">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-6">
                                                <div className="w-12 h-12 bg-purple-100 rounded-2xl flex items-center justify-center text-purple-700 font-black text-xl">
                                                    {customer.name?.charAt(0).toUpperCase()}
                                                </div>
                                                <div className="flex-1">
                                                    <h3 className="text-md text-[#1a1c1e]">{customer.name}</h3>
                                                    <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">#PAN_{customer.pan_number || 'N/A'}</p>
                                                </div>
                                                {(user?.issuperadmin || user?.issubadmin) && (
                                                    <button
                                                        onClick={() => handleViewDetails('customer', customer)}
                                                        className="px-6 py-2 bg-indigo-600 text-white rounded-full text-[11px] font-black uppercase transition-all hover:bg-indigo-700 active:scale-95 shadow-sm"
                                                    >
                                                        View Profile
                                                    </button>
                                                )}
                                            </div>
                                            <div className="grid grid-cols-2 md:grid-cols-3 gap-8 mt-4 text-sm">
                                                <div>
                                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">PAN Number</p>
                                                    <p className="text-gray-800 text-md">{customer.pan_number || 'N/A'}</p>
                                                </div>
                                                <div>
                                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Mobile Number</p>
                                                    <p className="text-gray-800 text-md">{customer.mobile_no || 'N/A'}</p>
                                                </div>
                                                <div>
                                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Email</p>
                                                    <p className="text-gray-800 text-md truncate max-w-[200px]">{customer.mail_id || 'N/A'}</p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="md:w-80 flex flex-col gap-4">
                                            {/* Identity Information Box */}
                                            <div className="bg-[#f8f9fa] rounded-2xl p-5 border border-gray-50">
                                                <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                                                    <i className="fas fa-id-card"></i> Identity Information
                                                </h4>
                                                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 mb-2">
                                                    <div className="space-y-3">
                                                        <div>
                                                            <p className="text-[9px] font-black text-gray-400 uppercase">PAN Number</p>
                                                            <p className="text-sm font-black text-gray-800">{customer.pan_number || 'N/A'}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-[9px] font-black text-gray-400 uppercase">DOB</p>
                                                            <p className="text-sm font-black text-gray-800">
                                                                {customer.dob && !customer.dob.includes('0000') ? new Date(customer.dob).toLocaleDateString('en-GB') : 'N/A'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    {(user?.issuperadmin || user?.issubadmin) && (
                                                        <button
                                                            onClick={() => handleViewDetails('customer', customer)}
                                                            className="mt-4 w-full py-2 bg-white border border-indigo-100 text-indigo-600 rounded-full text-[10px] font-black uppercase hover:bg-indigo-50 transition-all active:scale-95 shadow-sm"
                                                        >
                                                            View Details
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            {/* ITR Information Box */}
                                            {customer.itrs && customer.itrs.length > 0 && (
                                                <div className="bg-[#f8f9fa] rounded-2xl p-5 border border-gray-50">
                                                    <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                                                        <i className="fas fa-file-invoice"></i> ITR Information
                                                    </h4>
                                                    <div className="space-y-4">
                                                        {customer.itrs.map(itr => (
                                                            <div key={itr.id} className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                                                                <div className="flex justify-between items-start">
                                                                    <div>
                                                                        <span className="text-sm font-black text-gray-800 block">AY {itr.asst_year}</span>
                                                                        <p className="text-[10px] font-medium text-gray-400 mt-0.5 italic">
                                                                            Date: {itr.created_at && !itr.created_at.includes('0000') ? new Date(itr.created_at).toLocaleDateString('en-GB') : 'Invalid Date'}
                                                                        </p>
                                                                    </div>
                                                                    <span className={`text-[9px] font-black px-3 py-1 rounded-full uppercase ${(itr.status?.toLowerCase() === 'completed' || itr.status?.toLowerCase() === 'e-verification') ? 'bg-green-100 text-green-700' : 'bg-[#fff4e5] text-[#b45d00]'}`}>
                                                                        {itr.status || 'Pending'}
                                                                    </span>
                                                                </div>
                                                                {(user?.issuperadmin || user?.issubadmin) && (
                                                                    <button
                                                                        onClick={() => handleViewDetails('itr', itr)}
                                                                        className="mt-4 w-full py-2 bg-white border border-purple-100 text-[#7c3aed] rounded-full text-[10px] font-black uppercase hover:bg-purple-50 transition-all active:scale-95 shadow-sm"
                                                                    >
                                                                        View Details
                                                                    </button>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Expandable Details Section */}
                                    <div className="border-t border-gray-50 bg-gray-50/30 px-6 py-3">
                                        <button
                                            onClick={() => toggleExpand(customer.id)}
                                            className="text-[11px] font-black text-purple-700 uppercase tracking-widest flex items-center gap-2 hover:text-purple-900 transition-colors"
                                        >
                                            {expandedCards.has(customer.id) ? (
                                                <>
                                                    <i className="fas fa-chevron-up"></i> Hide Details
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-chevron-down"></i> View More Details
                                                </>
                                            )}
                                        </button>

                                        {expandedCards.has(customer.id) && (
                                            <div className={`mt-4 grid grid-cols-1 ${customer.itrs && customer.itrs.length > 0 ? 'md:grid-cols-2' : 'max-w-3xl'} gap-8 pb-4 animate-in slide-in-from-top-2 duration-300`}>
                                                {/* Extra Customer Info */}
                                                <div className="space-y-4">
                                                    <h5 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest border-b pb-1">Additional Profile Info</h5>
                                                    <div className="grid grid-cols-2 gap-4 text-[13px]">
                                                        <div>
                                                            <p className="text-[9px] font-bold text-gray-400 uppercase">Father's Name</p>
                                                            <p className="font-bold text-gray-700">{customer.father_name || 'N/A'}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-[9px] font-bold text-gray-400 uppercase">DOB</p>
                                                            <p className="font-bold text-gray-700">
                                                                {customer.dob ? new Date(customer.dob).toLocaleDateString('en-GB') : 'N/A'}
                                                            </p>
                                                        </div>
                                                        <div className="col-span-2">
                                                            <p className="text-[9px] font-bold text-gray-400 uppercase">Address</p>
                                                            <p className="font-bold text-gray-700 leading-tight">{customer.address || 'N/A'}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-[9px] font-bold text-gray-400 uppercase">Aadhaar Number</p>
                                                            <p className="font-bold text-gray-700">{customer.adhar_number || 'N/A'}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-[9px] font-bold text-gray-400 uppercase">Category</p>
                                                            <p className="font-bold text-gray-700">{customer.category || 'N/A'}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-[9px] font-bold text-gray-400 uppercase">Bank Account</p>
                                                            <p className="font-bold text-gray-700">{customer.account_number || 'N/A'}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-[9px] font-bold text-gray-400 uppercase">IFSC Code</p>
                                                            <p className="font-bold text-gray-700">{customer.ifsc_code || 'N/A'}</p>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Extra ITR Info */}
                                                {customer.itrs && customer.itrs.length > 0 && (
                                                    <div className="space-y-4">
                                                        <h5 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest border-b pb-1">Extended ITR Records</h5>
                                                        <div className="space-y-4">
                                                            {customer.itrs.map(itr => (
                                                                <div key={itr.id} className="bg-white p-3 rounded-xl border border-purple-100 shadow-sm">
                                                                    <div className="flex justify-between items-start mb-2">
                                                                        <p className="text-[11px] font-black text-purple-700 lowercase bg-purple-50 px-2 py-0.5 rounded-md">ay {itr.asst_year}</p>
                                                                        <p className="text-[9px] font-bold text-gray-400 uppercase">Ack: {itr.ack_no || 'Pending'}</p>
                                                                    </div>
                                                                    <div className="grid grid-cols-2 gap-2 text-[11px]">
                                                                        <div>
                                                                            <p className="text-[8px] font-bold text-gray-400 uppercase">ITR Password</p>
                                                                            <p className="font-bold text-gray-600 line-clamp-1">{itr.itr_password || 'N/A'}</p>
                                                                        </div>
                                                                        <div>
                                                                            <p className="text-[8px] font-bold text-gray-400 uppercase">Remark</p>
                                                                            <p className="font-bold text-gray-600 line-clamp-1">{itr.remark || 'N/A'}</p>
                                                                        </div>
                                                                        <div className="col-span-2">
                                                                            <p className="text-[8px] font-bold text-gray-400 uppercase">Description</p>
                                                                            <p className="text-gray-600 leading-tight italic">{itr.description || 'No additional description provided.'}</p>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-20 text-center">
                            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                                <i className="fas fa-search text-gray-200 text-3xl"></i>
                            </div>
                            <h3 className="text-xl font-black text-gray-800">No Customers Found</h3>
                            <p className="text-gray-500 max-w-sm mt-2 font-medium">We couldn't find any customers matching your search query.</p>
                            <button
                                onClick={handleBack}
                                className="mt-6 px-6 py-2 bg-purple-600 text-white rounded-xl font-bold hover:shadow-lg transition-all"
                            >
                                Return to Dashboard
                            </button>
                        </div>
                    )
                ) : (
                    filteredItrs.length > 0 ? (
                        <div className="grid gap-6">
                            {filteredItrs.map(itr => (
                                <div key={itr.id} className="bg-white border rounded-3xl shadow-sm hover:shadow-md transition-all overflow-hidden border-gray-100 mb-2">
                                    <div className="pt-6 px-6 pb-6 flex flex-col md:flex-row justify-between gap-6">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-6">
                                                <div className="w-12 h-12 bg-indigo-100 rounded-2xl flex items-center justify-center text-indigo-700 font-black text-xl">
                                                    <i className="fas fa-file-invoice"></i>
                                                </div>
                                                <div className="flex-1">
                                                    <h3 className="text-md text-[#1a1c1e] font-bold">AY {itr.asst_year}</h3>
                                                    <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">{itr.customer_details?.name || 'Unknown Customer'}</p>
                                                </div>
                                                <span className={`text-[10px] font-black px-4 py-1.5 rounded-full uppercase ${(itr.status?.toLowerCase() === 'completed' || itr.status?.toLowerCase() === 'e-verification') ? 'bg-green-100 text-green-700' : 'bg-[#fff4e5] text-[#b45d00]'}`}>
                                                    {itr.status || 'Pending'}
                                                </span>
                                                {(user?.issuperadmin || user?.issubadmin) && (
                                                    <button
                                                        onClick={() => handleViewDetails('itr', itr)}
                                                        className="px-6 py-2 bg-indigo-600 text-white rounded-full text-[11px] font-black uppercase transition-all hover:bg-indigo-700 active:scale-95 shadow-sm"
                                                    >
                                                        View Details
                                                    </button>
                                                )}
                                            </div>
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-4 text-sm bg-gray-50/50 p-4 rounded-xl border border-gray-100">
                                                <div>
                                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">PAN</p>
                                                    <p className="text-gray-800 text-md font-mono">{itr.pan_number || 'N/A'}</p>
                                                </div>
                                                <div>
                                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Acknowledgement</p>
                                                    <p className="text-gray-800 text-md">{itr.ack_no || 'Pending'}</p>
                                                </div>
                                                <div>
                                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Registered Date</p>
                                                    <p className="text-gray-800 text-md">{itr.created_at ? new Date(itr.created_at).toLocaleDateString('en-GB') : 'N/A'}</p>
                                                </div>
                                                <div>
                                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Agent</p>
                                                    <p className="text-gray-800 text-md">{itr.agent_name || 'N/A'}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-20 text-center">
                            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                                <i className="fas fa-file-invoice-dollar text-gray-200 text-3xl"></i>
                            </div>
                            <h3 className="text-xl font-black text-gray-800">No ITRs Found</h3>
                            <p className="text-gray-500 max-w-sm mt-2 font-medium">We couldn't find any ITR records matching your search query.</p>
                            <button
                                onClick={handleBack}
                                className="mt-6 px-6 py-2 bg-purple-600 text-white rounded-xl font-bold hover:shadow-lg transition-all"
                            >
                                Return to Dashboard
                            </button>
                        </div>
                    )
                )}
            </div>

            {/* Inline Detail Modal */}
            {viewingDetail && (
                <div className="fixed inset-0 pt-20 bg-black/80 backdrop-blur-md transition-opacity duration-300 overflow-y-auto h-full px-4 w-full z-[100] flex items-center justify-center">
                    <div className="relative mx-auto border-none w-full max-w-4xl shadow-2xl rounded-2xl bg-white overflow-hidden transform transition-all scale-100 max-h-[90vh] flex flex-col">
                        {/* Header */}
                        <div className={`px-6 py-4 text-white flex justify-between items-center shrink-0 ${detailType === 'customer' ? 'bg-gradient-to-r from-indigo-600 to-indigo-800' : 'bg-gradient-to-r from-purple-600 to-purple-800'}`}>
                            <div className="flex items-center gap-4">
                                <div className="bg-white/20 p-2 rounded-xl backdrop-blur-sm">
                                    <i className={`fas ${detailType === 'customer' ? 'fa-user' : 'fa-file-invoice'} text-xl`}></i>
                                </div>
                                <div>
                                    <h3 className="text-xl font-black tracking-tight uppercase">
                                        {detailType === 'customer' ? 'Customer Profile' : 'ITR Record Detail'}
                                    </h3>
                                    <p className="text-white/80 text-[10px] font-bold tracking-widest uppercase">
                                        {detailType === 'customer' ? viewingDetail.name : `AY ${viewingDetail.asst_year} - ${viewingDetail.pan_number}`}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={closeDetailModal}
                                className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-all hover:rotate-90"
                            >
                                <IoMdClose size={24} />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
                            {detailType === 'customer' ? (
                                <div className="space-y-8">
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block">Full Name</label>
                                            <p className="text-sm font-bold text-gray-800">{viewingDetail.name || 'N/A'}</p>
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block">PAN Number</label>
                                            <p className="text-sm font-bold text-gray-800 font-mono tracking-tighter uppercase">{viewingDetail.pan_number || 'N/A'}</p>
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block">Mobile Number</label>
                                            <p className="text-sm font-bold text-gray-800">{viewingDetail.mobile_no || 'N/A'}</p>
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block">Email Address</label>
                                            <p className="text-sm font-bold text-gray-800">{viewingDetail.mail_id || 'N/A'}</p>
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block">Father's Name</label>
                                            <p className="text-sm font-bold text-gray-800">{viewingDetail.father_name || 'N/A'}</p>
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block">Date of Birth</label>
                                            <p className="text-sm font-bold text-gray-800">
                                                {viewingDetail.dob ? new Date(viewingDetail.dob).toLocaleDateString('en-GB') : 'N/A'}
                                            </p>
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block">Aadhaar Number</label>
                                            <p className="text-sm font-bold text-gray-800">{viewingDetail.adhar_number || 'N/A'}</p>
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block">Category</label>
                                            <p className="text-sm font-bold text-gray-800">{viewingDetail.category || 'N/A'}</p>
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block">User ID / Password</label>
                                            <p className="text-sm font-bold text-gray-800">{viewingDetail.user_id || 'N/A'} / {viewingDetail.itr_password || 'N/A'}</p>
                                        </div>
                                    </div>

                                    <div className="pt-6 border-t border-gray-100">
                                        <h4 className="text-[11px] font-black text-gray-400 uppercase tracking-widest mb-4">Bank & Financial Information</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block">Bank Name</label>
                                                <p className="text-sm font-bold text-gray-800">{viewingDetail.bank_name || 'N/A'}</p>
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block">Account Number</label>
                                                <p className="text-sm font-bold text-gray-800">{viewingDetail.account_number || 'N/A'}</p>
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block">IFSC Code</label>
                                                <p className="text-sm font-bold text-gray-800 uppercase">{viewingDetail.ifsc_code || 'N/A'}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="pt-6 border-t border-gray-100">
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2">Residential Address</label>
                                        <p className="text-sm font-bold text-gray-700 bg-gray-50 p-4 rounded-xl border border-gray-100 leading-relaxed">
                                            {viewingDetail.address || 'No address provided.'}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-8">
                                    <div className="flex flex-wrap items-center gap-4">
                                        <div className="bg-purple-50 px-4 py-2 rounded-2xl border border-purple-100">
                                            <span className="text-[10px] font-black text-purple-400 uppercase block">Assessment Year</span>
                                            <span className="text-2xl font-black text-purple-800">AY {viewingDetail.asst_year}</span>
                                        </div>
                                        <div className={`${(viewingDetail.status?.toLowerCase() === 'completed' || viewingDetail.status?.toLowerCase() === 'e-verification') ? 'bg-green-50 border-green-100 text-green-700' : 'bg-orange-50 border-orange-100 text-orange-700'} px-4 py-2 rounded-2xl border flex-1 min-w-[150px]`}>
                                            <span className="text-[10px] font-black opacity-60 uppercase block">Current Status</span>
                                            <span className="text-lg font-black uppercase tracking-tight">{viewingDetail.status || 'Pending'}</span>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-4">
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block">PAN Number</label>
                                            <p className="text-sm font-bold text-gray-800 font-mono">{viewingDetail.pan_number || 'N/A'}</p>
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block">Filing Type</label>
                                            <p className="text-sm font-bold text-gray-800 uppercase">{viewingDetail.filling_type || 'N/A'}</p>
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block">Income Slab</label>
                                            <p className="text-sm font-bold text-gray-800">{viewingDetail.income_slab || 'N/A'}</p>
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block">ITR Password</label>
                                            <p className="text-sm font-bold text-gray-800">{viewingDetail.itr_password || 'N/A'}</p>
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block">Acknowledgement No</label>
                                            <p className="text-sm font-bold text-gray-800">{viewingDetail.ack_no || 'Pending'}</p>
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block">Registered Date</label>
                                            <p className="text-sm font-bold text-gray-800">
                                                {viewingDetail.created_at ? new Date(viewingDetail.created_at).toLocaleString('en-GB') : 'N/A'}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="pt-6 border-t border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block">Description</label>
                                            <div className="text-sm text-gray-600 bg-gray-50 p-4 rounded-xl border border-gray-100 min-h-[80px] leading-relaxed">
                                                {viewingDetail.description || 'No additional description.'}
                                            </div>
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block">Internal Remark</label>
                                            <div className="text-sm text-gray-600 bg-gray-50 p-4 rounded-xl border border-gray-100 min-h-[80px] leading-relaxed italic">
                                                {viewingDetail.remark || 'No internal remarks.'}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Additional fields for ITR if available */}
                                    <div className="pt-6 border-t border-gray-100">
                                        <h4 className="text-[11px] font-black text-gray-400 uppercase tracking-widest mb-4">Tracking Information</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 uppercase">
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-black text-gray-400 tracking-widest block">Agent Name</label>
                                                <p className="text-xs font-bold text-gray-700">{viewingDetail.agent_name || 'N/A'}</p>
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-black text-gray-400 tracking-widest block">Subadmin</label>
                                                <p className="text-xs font-bold text-gray-700">{viewingDetail.subadmin_username || 'N/A'}</p>
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-black text-gray-400 tracking-widest block">Assigned CA</label>
                                                <p className="text-xs font-bold text-gray-700">{viewingDetail.ca_name || 'Not Assigned'}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="p-6 bg-gray-50 border-t border-gray-100 shrink-0">
                            <button
                                onClick={closeDetailModal}
                                className="w-full py-4 bg-gray-800 text-white rounded-xl font-black text-xs uppercase tracking-[0.2em] hover:bg-black transition-all shadow-lg active:scale-95"
                            >
                                Back to Results
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GlobalSearchOverlay;
